import { AxiosError } from 'axios';
import { IDoryConfig } from '../interface/IDoryConfig';
import { Ajax } from '../ajax/Ajax';
import { DefaultAuthParameter, DefaultLogin } from './providers/DefaultLogin';
import {
  AuthProvider,
  SessionExpiredListener,
  TokenRefreshListener,
} from './providers/AuthProvider';
import { SsoAuthParameter, SsoLogin } from './providers/SsoLogin';
import { ApiKeyLogin, ApiKeyAuthParameter } from './providers/ApiKeyLogin';
import { SamlAuthParameter, SamlLogin } from './providers/SamlLogin';
import {
  BrokerageAuthParameter,
  BrokerageLogin,
} from './providers/BrokerageLogin';

//#region Interfaces
/**
 * Describes the structure of the JWT
 */
export type ITokenObject = {
  exp: number;
  sub: string;
  nbf: number;
  iss: string;
  iat: number;
  jti: string;
  msLeft: number;
  prm: string[];
};
export type ISessionObject = {
  user: string;
  isSSO: boolean;
  sid: string;
  exp: number;
  initialDuration: number;
  sid_core: string;
  sid_ngp: string;
  msLeft: number;
  userTimeOffset?: number;
  sessionLatency?: number;
  tokenLatency?: number;
  token?: string;
};

export type IAuthRequest = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type IAuthResponse = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
};
//#endregion

/**
 * This service offers authentication and authorization.
 * By using this service, you can give your own applications access to all SDK APIs.
 * The service offers a valid session and a JWT (JSON Web Tokens open industry standard RFC 7519) token.
 */
export class Auth extends Ajax {
  private config: IDoryConfig;
  /**
   * @hidden
   */
  public get Config(): IDoryConfig {
    return this.config;
  }

  private cookieSession = false;
  /**
   * @hidden
   */
  public get CookieSession(): boolean {
    return this.cookieSession;
  }

  private authProvider: AuthProvider<
    | DefaultAuthParameter
    | BrokerageAuthParameter
    | SsoAuthParameter
    | SamlAuthParameter
    | ApiKeyAuthParameter
  >;
  /**
   * @hidden
   */
  public get AuthProvider(): AuthProvider<
    | DefaultAuthParameter
    | BrokerageAuthParameter
    | SsoAuthParameter
    | SamlAuthParameter
    | ApiKeyAuthParameter
  > {
    return this.authProvider;
  }

  /**
   * Auth Constructor
   * @hidden
   *
   * @param config base configuration interface instance
   */
  constructor(config: IDoryConfig) {
    super(config.auth);
    /**
     * Set this.auth(member of Ajax class) to the current instance of the Auth Class which is used by Rest & Admin classes
     */
    this.auth = this;
    this.config = config;
    this.authProvider = new DefaultLogin(this.auth);
  }

  //#region Endpoints
  /**
   * Set auth endpoint
   * @category Endpoint
   *
   * @param newEndpoint  Full url to new endpoint
   */
  public setBaseUrlEndpoint(newEndpoint: string): void {
    this.config.auth = {
      url: newEndpoint,
      urlToken: `${newEndpoint}/tokens`,
      urlSession: `${newEndpoint}/sessions`,
    };
  }
  /**
   * Get auth endpoint
   * @category Endpoint
   */
  public getBaseUrlEndpoint(): string {
    return this.config.auth.url;
  }

  /**
   * Set session-server endpoint
   * @category Endpoint
   *
   * @param newEndpoint  Full url to new endpoint
   */
  public setSessionEndpoint(newEndpoint: string): void {
    this.config.auth.urlSession = newEndpoint;
  }
  /**
   * Get session-server endpoint
   * @category Endpoint
   */
  public getSessionEndpoint(): string {
    return this.config.auth.urlSession;
  }

  /**
   * Set token-server endpoint
   * @category Endpoint
   *
   * @param newEndpoint  Full url to new endpoint
   */
  public setTokenEndpoint(newEndpoint: string): void {
    this.config.auth.urlToken = newEndpoint;
  }
  /**
   * Get token-server endpoint
   * @category Endpoint
   */
  public getTokenEndpoint(): string {
    return this.config.auth.urlToken;
  }
  //#endregion

  //#region Session/Token Handling
  /**
   * Get the encoded token-string from localStorage
   */
  public getTokenString(): Promise<string> {
    return this.authProvider.getTokenString();
  }

  /**
   * Get the decoded token from localstorage
   *
   * ```js example
   * dory.Auth.getToken()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *     // > { did, ety, exp, grp, iat, iss, jti, msLeft, nbf }
   *   });
   * ```
   */
  public getToken(): Promise<ITokenObject> {
    return this.authProvider.getToken();
  }

  /**
   * Check if token is within its expiration time
   *
   * ```js example
   * dory.Auth.isTokenValid()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   */
  public async isTokenValid(): Promise<boolean> {
    return this.getToken()
      .catch((error) => {
        throw new Error(`Failed to get token: ${error}`);
      })
      .then((to) => {
        return !!(to && to.msLeft > 30000);
      });
  }

  /**
   * Call this function before making a request to a rest-service, it makes sure that you have
   * a valid token. (see Rest)
   */
  public async validateToken(): Promise<boolean> {
    return this.isTokenValid()
      .catch((error) => {
        return Promise.reject(`Failed validate token: ${error}`);
      })
      .then((isValid) => {
        if (!isValid) {
          return this.authProvider.refreshToken();
        }

        return true;
      });
  }

  /**
   * This function requests a new token with the session stored in localStorage
   *
   * ```js example
   * dory.Auth.refreshToken()
   *  .catch(function (error) {
   *    console.error(error); // catch api errors
   *  })
   *  .then(function (result) {
   *    console.log(result);
   *  });
   * ```
   */
  public async refreshToken(): Promise<boolean> {
    return this.authProvider.refreshToken();
  }

  /**
   * @hidden
   */
  public useSessionCookie(): void {
    this.cookieSession = true;
  }

  /**
   * Get session details from localstorage
   *
   * ```js example
   * dory.Auth.getSession()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *     // > { exp, msLeft, sid, sid_core, sid_ngp, user }
   *   });
   * ```
   */
  public getSession(): Promise<ISessionObject> {
    return this.authProvider.getSession();
  }

  /**
   * Get session from localstorage and check if it's still valid
   */
  public isSessionValid(): Promise<boolean> {
    return this.authProvider.isSessionValid();
  }

  /**
   * Register a callback function to get informed when the Dory Session expired.
   *
   * @param listener The function that receives the notification
   */
  public addSessionExpiredListener(listener: SessionExpiredListener): void {
    this.authProvider.sessionExpiredListeners.push(listener);
  }

  /**
   * Deregister the session expired Listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeSessionExpiredListener(listener: SessionExpiredListener): void {
    const filterListeners = (l: SessionExpiredListener) => l !== listener;

    this.authProvider.sessionExpiredListeners =
      this.authProvider.sessionExpiredListeners.filter(filterListeners);
  }

  /**
   * Register a callback function to get informed when the token refreshed.
   *
   * @param listener The function that receives the notification
   */
  public addTokenRefreshListener(listener: TokenRefreshListener): void {
    this.authProvider.tokenRefreshListeners.push(listener);
  }

  /**
   * Deregister the token refresh listener
   *
   * @param listener The EventListener function of the event handler to remove from the event target.
   */
  public removeTokenRefreshListener(listener: TokenRefreshListener): void {
    const filterListeners = (l: TokenRefreshListener) => l !== listener;

    this.authProvider.tokenRefreshListeners =
      this.authProvider.tokenRefreshListeners.filter(filterListeners);
  }

  //#endregion

  /**
   * Use this function retrieve a session
   *
   * ```js example
   * dory.Auth.login('username', 'password', 'product')
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   *
   * @param user  The user name
   * @param pw  The secret user password
   * @param product  The name of the current product
   *
   * @returns The response of the server
   */
  public login(
    user: string,
    pw: string,
    product: string
  ): Promise<boolean | ITokenObject> {
    this.authProvider = new DefaultLogin(this.auth);
    return this.authProvider.login({
      username: user,
      password: pw,
      product,
    });
  }

  /**
   * Use this function to retrieve a brokerage session
   *
   * ```js example
   * dory.Auth.brokerageLogin('username@provider.com', 'password', 'product')
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   *
   * @param email  The registered email
   * @param pw  The secret user password
   * @param product  The name of the current product
   * @returns
   */
  public brokerageLogin(
    email: string,
    pw: string,
    product: string
  ): Promise<boolean | ITokenObject> {
    this.authProvider = new BrokerageLogin(this.auth);
    return this.authProvider.login({
      email,
      password: pw,
      product,
    });
  }

  /**
   * Use this function retrieve a session via SSO
   *
   * @param cipher  SSO Cipher
   * @param hash  SSO Hash
   * @param authgroup  Authgroup to use
   *
   * @returns The response of the server
   */
  public ssoLogin(
    cipher: string,
    hash: string,
    authgroup: string
  ): Promise<boolean | ITokenObject> {
    this.authProvider = new SsoLogin(this.auth);
    return this.authProvider.login({
      cipher,
      hash,
      authgroup,
    });
  }

  /**
   * Use this function retrieve a session via SAML
   *
   * @param SAMLResponse  SAML string to send to server
   *
   * @returns The response of the server
   */
  public samlLogin(SAMLResponse: string): Promise<boolean | ITokenObject> {
    this.authProvider = new SamlLogin(this.auth);
    return this.authProvider.login({ SAMLResponse });
  }

  /**
   * Use this function retrieve a jwt using X-API-KEY
   *
   * @param apiKey  API Key
   *
   * @returns The response of the server
   */
  public apiKeyLogin(apiKey: string): Promise<boolean | ITokenObject> {
    this.authProvider = new ApiKeyLogin(this.auth);
    return this.authProvider.login({ apiKey });
  }

  /**
   * Change the current password of the user.
   *
   * @param currentPassword  The current password
   * @param newPassword  The new password
   * @returns The response of the server
   */
  public changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<boolean> {
    const url = this.config.user.url + '/entities/logins/newpwd';
    return this.request({
      url,
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: this.RequestTimeout,
      data: {
        currentPassword,
        newPassword,
      },
    })
      .then(() => true)
      .catch((error: AxiosError) => {
        return Promise.reject(error);
      });
  }

  /**
   * Change the current password for first time user login. This will also generate a new session for this user.
   *
   * @param currentPassword  The current password
   * @param newPassword  The new password
   * @param user  The name of the user
   * @param product  The name of the product
   *
   * @returns The response of the server
   */
  public changePasswordFirstTime(
    currentPassword: string,
    newPassword: string,
    user: string,
    product: string
  ): Promise<ISessionObject> {
    this.authProvider = new DefaultLogin(this.auth);
    // this.UrlFirstTimePassword
    const url = this.config.user.url + '/entities/logins/newpwdFirstTimeUser';
    return this.request<{
      sid: string;
      expiresAt: number;
    }>({
      url,
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: this.RequestTimeout,
      data: {
        user,
        product,
        currentPassword,
        newPassword,
      },
    })
      .then(
        (response) =>
          new Promise<ISessionObject>((resolve, reject) => {
            if (this.authProvider.validateSessionResponse(response)) {
              this.setSessionObject({
                user,
                isSSO: false,
                sid: response.data.sid,
                exp: response.data.expiresAt,
                initialDuration: response.data.expiresAt * 1000 - Date.now(),
                sid_core: null,
                sid_ngp: null,
                msLeft: null,
              }).then(() => {
                this.authProvider.getSession().then((session) => {
                  resolve(session);
                });
              });
            } else {
              this.setSessionObject(null).then(() => {
                reject('MISSING_SESSIONID_IN_RESPONSE');
              });
            }
          })
      )
      .catch((error: AxiosError) => {
        return Promise.reject(error);
      });
  }

  /**
   * Sends a logout request and removes session and token data from localStorage
   *
   * ```js example
   * dory.Auth.logout()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   *
   * @returns The response of the server
   */
  public logout(): Promise<boolean> {
    return this.authProvider.logout();
  }

  private async setSessionObject(sobj: ISessionObject) {
    return this.authProvider.setSessionObject(sobj);
  }
}
